import { useState } from 'react'
import { useListsStore } from '../../stores/listsStore'
import type { Card } from '../../types'
import Button from '../ui/Button'

/**
 * CustomCardsPanel — allows the user to upload local images as custom cards.
 *
 * These cards are added to a special "Custom Cards" list that can be combined
 * with regular lists to fill gaps when the total count is not a multiple of 9.
 */
export default function CustomCardsPanel() {
  const addList = useListsStore((s) => s.addList)
  const lists = useListsStore((s) => s.lists)
  const setListCards = useListsStore((s) => s.setListCards)

  const [isOpen, setIsOpen] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const CUSTOM_LIST_NAME = '🎨 Cartas personalizadas'

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || [])
    if (files.length > 0) {
      setSelectedFiles((prev) => [...prev, ...files])
    }
  }

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const addCustomCards = () => {
    if (selectedFiles.length === 0) return

    // Convert files to Card objects
    const newCards: Card[] = selectedFiles.map((file) => ({
      name: file.name.replace(/\.[^/.]+$/, ''), // Remove extension
      quantity: 1,
      image_url: URL.createObjectURL(file),
      _localFile: file,
      game: 'mtg', // Use 'mtg' as placeholder TCG (will be overridden by list's TCG when combined)
    }))

    // Find or create the custom cards list
    const existingList = lists.find((l) => l.name === CUSTOM_LIST_NAME)
    if (existingList) {
      // Append to existing list
      setListCards(existingList.id, [...existingList.mainboard, ...newCards], existingList.sideboard)
    } else {
      // Create new list
      addList(CUSTOM_LIST_NAME, newCards, [], 'mtg') // Use 'mtg' as placeholder TCG
    }

    setSelectedFiles([])
    setIsOpen(false)
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header with toggle */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-lg">Cartas personalizadas</h3>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
            Para rellenar huecos
          </span>
        </div>
        <span className="text-gray-400 text-xl leading-none">{isOpen ? '−' : '+'}</span>
      </button>

      {/* Collapsible content */}
      {isOpen && (
        <div className="p-4 pt-0 space-y-4 border-t border-gray-100">
          <p className="text-sm text-gray-600">
            Sube imágenes de cartas personalizadas o encontradas por ahí para rellenar
            los huecos cuando tu lista no sea múltiplo de 9.
          </p>

          {/* File input */}
          <div>
            <label className="block w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
              <span className="text-sm text-gray-600">
                📷 Haz clic para seleccionar imágenes (o arrastra aquí)
              </span>
            </label>
          </div>

          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">
                {selectedFiles.length} imagen(es) seleccionada(s):
              </p>
              <ul className="space-y-1 max-h-40 overflow-y-auto">
                {selectedFiles.map((file, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between text-sm bg-gray-50 px-3 py-2 rounded"
                  >
                    <span className="truncate text-gray-700">{file.name}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-red-500 hover:text-red-700 ml-2 shrink-0"
                      title="Quitar"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
              <div className="flex gap-2 pt-2">
                <Button onClick={addCustomCards}>
                  Añadir a lista personalizada
                </Button>
                <button
                  type="button"
                  onClick={() => setSelectedFiles([])}
                  className="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-600 hover:bg-gray-50"
                >
                  Limpiar
                </button>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            💡 Las cartas personalizadas se añaden a una lista especial llamada
            "{CUSTOM_LIST_NAME}" que puedes combinar con tus otras listas.
          </p>
        </div>
      )}
    </div>
  )
}
